<template>
  <BaseSidebar
    :model-value="store.drawerPackageId !== null"
    width="560px"
    background-color="#FFFFFF"
    @update:model-value="onToggle"
  >
    <template #header="{ close }">
      <div class="flex flex-col items-start gap-3 p-6 pb-0 text-left">
        <button
          type="button"
          class="text-textPrimary"
          aria-label="Fechar"
          data-testid="affiliate-drawer-close"
          @click="close"
        >
          <svg class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path :d="mdiArrowRight" />
          </svg>
        </button>
        <h2 class="m-0 flex flex-wrap items-center gap-2.5 text-2xl font-bold text-textPrimary">
          <svg class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path :d="mdiPackageVariantClosed" />
          </svg>
          Pacote <span class="font-mono">#{{ store.drawerPackageId }}</span>
          <AffiliateSplitTags
            v-if="pkg"
            :origin-package-id="pkg.originPackageId"
            :split-to-package-ids="pkg.splitToPackageIds"
          />
        </h2>
        <div v-if="pkg" class="flex flex-wrap items-center gap-2">
          <AffiliateSlaBadge :state="liveSlaState(pkg, nowMs)" />
          <AffiliateStageCell :stage="pkg.stage" :show-steps="false" />
        </div>
      </div>
    </template>

    <div
      class="flex flex-col gap-[18px] pb-6 pt-4 text-left"
      data-testid="affiliate-package-drawer"
    >
      <div
        v-if="store.isLoadingDrawer"
        class="h-40 animate-pulse rounded-lg bg-lightGray30"
        data-testid="affiliate-drawer-loading"
      />
      <div
        v-else-if="store.drawerError"
        class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
        data-testid="affiliate-drawer-error"
      >
        {{ store.drawerError }}
      </div>
      <template v-else-if="detail && pkg">
        <div class="grid grid-cols-2 gap-x-4 gap-y-2.5">
          <div class="flex flex-col gap-0.5">
            <span class="text-[11px] font-semibold uppercase tracking-wider text-neutral50"
              >Médico</span
            >
            <span class="font-semibold">{{ pkg.doctorName }}</span>
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="text-[11px] font-semibold uppercase tracking-wider text-neutral50"
              >Unidade</span
            >
            <span class="font-semibold">{{ formatUnitNames(pkg.units) }}</span>
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="text-[11px] font-semibold uppercase tracking-wider text-neutral50"
              >Pedido em</span
            >
            <span class="font-semibold tabular-nums">{{ formatSlaDateTime(pkg.requestedAt) }}</span>
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="text-[11px] font-semibold uppercase tracking-wider text-neutral50">
              {{ pkg.paidAt ? 'Tempo até pagar' : `Prazo (${slaHours} h)` }}
            </span>
            <AffiliatePackageClock :row="pkg" :now-ms="nowMs" test-id="affiliate-drawer-clock" />
          </div>
        </div>

        <div>
          <span class="text-[11px] font-semibold uppercase tracking-wider text-neutral50"
            >Linha do tempo</span
          >
          <ol class="m-0 mt-2.5 flex list-none flex-col p-0">
            <li
              v-for="(entry, index) in timeline"
              :key="entry.key"
              class="relative grid grid-cols-[22px_1fr] gap-2.5 pb-3.5"
              :data-testid="`affiliate-timeline-${entry.key}`"
            >
              <span
                v-if="index < timeline.length - 1"
                class="absolute bottom-0 left-2.5 top-[22px] w-0.5 bg-lightGray30"
                aria-hidden="true"
              />
              <span
                class="grid h-[22px] w-[22px] place-items-center rounded-full"
                :class="DOT_CLASS[entry.tone]"
              >
                <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path :d="entry.icon" />
                </svg>
              </span>
              <div>
                <div class="font-semibold">{{ entry.title }}</div>
                <div v-if="entry.when" class="text-xs text-neutral50">{{ entry.when }}</div>
                <div v-if="entry.description || entry.linkPackageId" class="text-xs text-neutral50">
                  {{ entry.description }}
                  <button
                    v-if="entry.linkPackageId"
                    type="button"
                    class="font-semibold text-primary underline underline-offset-2"
                    :data-testid="`affiliate-timeline-link-${entry.linkPackageId}`"
                    @click="openLinkedPackage(entry.linkPackageId)"
                  >
                    Abrir #{{ entry.linkPackageId }}
                  </button>
                </div>
              </div>
            </li>
          </ol>
        </div>

        <div>
          <span class="text-[11px] font-semibold uppercase tracking-wider text-neutral50"
            >Plantões neste pacote</span
          >
          <div class="mt-2 flex flex-col rounded-lg border border-lightGray30">
            <div
              v-for="shift in detail.shifts"
              :key="shift.doctorStationId"
              class="flex flex-wrap justify-between gap-2.5 border-b border-lightGray30 px-3 py-2.5 last:border-b-0"
              :data-testid="`affiliate-drawer-shift-${shift.doctorStationId}`"
            >
              <span>
                <b class="tabular-nums">{{ formatDayMonth(shift.date) }}</b> ·
                {{ shift.startHour }}–{{ shift.endHour }}
                <span
                  v-if="!shift.pertinent"
                  class="ml-1 rounded border border-current px-1.5 text-[11px] font-bold text-neutral50"
                >
                  Fora das suas unidades
                </span>
              </span>
              <span class="text-xs text-neutral50"
                >{{ shift.scheduleName }} · {{ shift.unitName }}</span
              >
            </div>
            <div
              v-for="moved in detail.movedShifts"
              :key="`moved-${moved.doctorStationId}`"
              class="flex flex-wrap justify-between gap-2.5 border-b border-lightGray30 bg-orange-50 px-3 py-2.5 last:border-b-0"
              :data-testid="`affiliate-drawer-moved-${moved.doctorStationId}`"
            >
              <span>
                <b class="tabular-nums">{{ formatDayMonth(moved.date) }}</b> ·
                {{ moved.startHour }}–{{ moved.endHour }}
                <span
                  class="ml-1 rounded border border-current px-1.5 text-[11px] font-bold text-orange-700"
                  >Movido</span
                >
              </span>
              <span class="text-xs text-neutral50">
                Recusado na conferência · agora no
                <button
                  type="button"
                  class="font-semibold text-primary underline underline-offset-2"
                  :data-testid="`affiliate-moved-link-${moved.toPackageId}`"
                  @click="openLinkedPackage(moved.toPackageId)"
                >
                  #{{ moved.toPackageId }}
                </button>
              </span>
            </div>
          </div>
        </div>

        <p class="m-0 flex items-center gap-1.5 text-xs text-neutral50">
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path :d="mdiLockOutline" />
          </svg>
          Valores, taxas e percentuais deste pacote não são exibidos no portal.
        </p>
      </template>
    </div>
  </BaseSidebar>
</template>

<script setup lang="ts">
import { mdiArrowRight, mdiLockOutline, mdiPackageVariantClosed } from '@mdi/js'
import { computed, onMounted, onUnmounted } from 'vue'

import BaseSidebar from '@/components/designSystem/Sidebar/BaseSidebar.vue'
import { formatSlaDateTime } from '@/modules/company/Approvals/PriorityPayments/slaFormat'
import { useAntecipeAffiliateStore } from '@/pinia-store/useAntecipeAffiliateStore'

import { formatDayMonth, formatUnitNames, liveSlaState } from '../format'
import { buildTimeline } from '../timeline'
import type { TimelineTone } from '../timeline'
import { useNowTick } from '../useNowTick'
import AffiliatePackageClock from './AffiliatePackageClock.vue'
import AffiliateSlaBadge from './AffiliateSlaBadge.vue'
import AffiliateSplitTags from './AffiliateSplitTags.vue'
import AffiliateStageCell from './AffiliateStageCell.vue'

const store = useAntecipeAffiliateStore()
const { nowMs } = useNowTick()

const detail = computed(() => store.drawer)
const pkg = computed(() => store.drawer?.package ?? null)
const timeline = computed(() => (store.drawer ? buildTimeline(store.drawer) : []))
const slaHours = computed(() => store.me?.slaHours ?? '')

const DOT_CLASS: Record<TimelineTone, string> = {
  done: 'bg-primary text-white',
  warn: 'bg-orange-100 text-orange-800',
  pending: 'bg-lightGray30 text-neutral50'
}

// Ruling F4: link sem pacote relacionado (null) não faz nada.
function openLinkedPackage(id: number | null): void {
  if (id !== null) void store.openPackage(id)
}

function onToggle(open: boolean): void {
  if (!open) store.closePackage()
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && store.drawerPackageId !== null) store.closePackage()
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))
</script>
